'use client';

import React, {
  FC,
  useEffect,
  useRef,
  useState,
  memo,
  useCallback,
} from 'react';
import { useIsVisible } from '@/hooks/useIsVisible';

import Image from 'next/image';

import Slider, { Settings } from 'react-slick';

import { Document } from '@contentful/rich-text-types';

import {
  ChevronIcon,
  LuvImage,
  RichText,
  Video,
  VideoEmbed,
} from '@/components';
import { MuxVideo } from '@/components/ui/MuxVideo';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';

import useCarouselContext from '@/context/CarouselContext';

import { CAROUSEL_SLIDES_TO_SHOW } from '@/core/constants';

import {
  TypeImage,
  TypeMediaTextTitle,
  TypeVideo,
  TypeVideoEmbedFields,
} from '@/core/generated-types';

import { useMatchesMediaQuery } from '@/hooks/useMatchesMediaQuery';

import PlayIcon from '@/public/assets/icons/play.svg';
import {
  track,
  PRODUCT_EVENT_TYPES,
  PRODUCT_ADDED_PAGE_TYPES,
} from '@/utils/rudderstack';
import { TypeMuxVideo, TypeMuxVideoFields } from '@/core/generated-types';

type SLIDER_TYPE = Slider | undefined;

interface ThumbnailArrowProps {
  direction: 'right' | 'left';
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const ThumbnailArrow = memo(
  ({ direction = 'right', onClick = () => null }: ThumbnailArrowProps) => {
    const style = direction === 'right' ? 'right-[-64px]' : 'left-[-42px]';

    return (
      <button
        className={`button absolute ${style} top-[28%] h-[32px] w-[32px] bg-no-repeat`}
        aria-label={direction == 'right' ? 'Next' : 'Previous'}
        onClick={onClick}
      >
        <ChevronIcon
          classes="mt-1 mr-4 pl-1 md:pl-0 min-w-[16px]"
          direction={direction}
        />
      </button>
    );
  },
);

ThumbnailArrow.displayName = 'ThumbnailArrow';

interface MediaSlideVideo extends TypeVideo {
  hasControls?: boolean;
}

const MediaSlide: FC<
  (TypeImage | MediaSlideVideo | TypeMediaTextTitle) & {
    isThumbnail?: boolean;
    index?: number;
    name?: string;
    pageType?: string;
    width?: number;
  }
> = memo((content) => {
  const mediaRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);
  const { isThumbnail, index, name, pageType, width } = content;

  const isVisible = useIsVisible(mediaRef, 0.5);

  // Add ref and visibility check for muxVideo
  const muxVideoRef = useRef<HTMLDivElement>(null);
  const isMuxVideoVisible = useIsVisible(muxVideoRef, 0.5);

  useEffect(() => {
    if (!mediaRef.current || isThumbnail) return;

    let timeoutId: NodeJS.Timeout | undefined;

    if (isVisible && !hasTrackedRef.current) {
      timeoutId = setTimeout(() => {
        track(PRODUCT_EVENT_TYPES.MEDIA_VIEWED, {
          media_index: index,
          name: name,
          pageType: pageType || PRODUCT_ADDED_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
        });
        hasTrackedRef.current = true;
      }, 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isVisible, index, name, pageType, isThumbnail]);

  const contentTypeId = content.sys?.contentType?.sys?.id;

  const isMobile = useMatchesMediaQuery({
    query: `(max-width: 640px)`,
  });

  switch (contentTypeId) {
    case 'image':
      const entryImage = content as TypeImage;

      return (
        <div
          ref={mediaRef}
          className="flex items-center p-2"
          data-imageid={content.sys.id}
        >
          <LuvImage
            imageData={entryImage}
            alt={entryImage.fields?.altText}
            width={width || 420}
            loading={isThumbnail ? 'lazy' : 'eager'}
          />
        </div>
      );
    case 'video':
      const entryVideo = content as MediaSlideVideo;
      const url = entryVideo?.fields?.video?.fields?.file?.url;
      const poster = entryVideo?.fields?.poster?.fields?.file?.url;
      const { hasControls: hasControlsProp } = entryVideo;
      const hideControls = entryVideo?.fields?.hideControls;

      const controls =
        hideControls === undefined ? hasControlsProp : !hideControls;

      return (
        <div ref={mediaRef} className="p-2 flex items-center">
          <Video
            poster={poster}
            src={`https:${url}`}
            autoplay={true}
            loop={true}
            muted={true}
            playsinline={isMobile}
            controls={controls}
          />
        </div>
      );
    case 'videoEmbed':
      const { platform, componentName, videoId, videoParams } =
        content.fields as TypeVideoEmbedFields;

      if (isThumbnail) {
        const thumbnail = (content.fields as TypeVideoEmbedFields).thumbnail
          ?.fields.file.url;

        return (
          <div className="flex items-center relative">
            {thumbnail ? (
              <span className="brightness-75">
                <Image
                  src={`https:${thumbnail}`}
                  alt="Video Thumbnail"
                  width="100"
                  height="80"
                />
              </span>
            ) : (
              <div className="bg-[#D9D9D9] p-2 flex min-h-12 w-16 mx-auto" />
            )}
            <Image
              src={PlayIcon.src}
              alt="Video Play icon"
              className="!h-5 !border-none brightness-0 invert absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              width="100"
              height="80"
            />
          </div>
        );
      }
      return (
        <div ref={mediaRef} className="p-2">
          <VideoEmbed
            platform={platform}
            componentName={componentName}
            videoId={videoId}
            entryName={videoId}
            videoParams={videoParams}
            classes="w-full h-[270px] md:h-[420px]"
          />
        </div>
      );
    case 'mediaTextTitle':
      const entryMedia = content as TypeMediaTextTitle;
      const { entryName, image, textHeading, textBody } = entryMedia.fields;

      if (!image) return null;

      return (
        <div
          ref={mediaRef}
          className="flex items-center justify-center pb-12 pt-2"
          data-imageid={content.sys.id}
        >
          <div>
            <LuvImage
              entryName={entryName}
              Image={image?.fields?.Image}
              altText={textHeading}
              width={200}
              height={200}
              className="media-text-title-img"
              loading="eager"
            />
            <div className="mt-1 px-6 text-center">
              <Heading
                text={textHeading}
                tag={HeadingTag.H3}
                size={HeadingSize.H3}
              />
              {textBody && (
                <div className="mt-1">
                  <RichText content={textBody as Document} />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case 'muxVideo':
      const entryMuxVideo = content as TypeMuxVideo;
      const playbackId =
        entryMuxVideo &&
        (entryMuxVideo.fields as TypeMuxVideoFields)?.muxVideoUpload
          ?.playbackId;

      const props = {
        title: entryMuxVideo.fields.entryName || '',
        playbackId,
        autoplay: isMuxVideoVisible && !isThumbnail,
        controls: !isThumbnail && !entryMuxVideo.fields.hideControls,
      };

      const thumbnail = (entryMuxVideo.fields as TypeMuxVideoFields).poster
        ?.fields.file.url;

      if (isThumbnail && thumbnail) {
        return (
          <div className="flex items-center relative">
            <span className="brightness-75">
              <Image
                src={`https:${thumbnail}`}
                alt="Video Thumbnail"
                width="100"
                height="80"
              />
            </span>
            <Image
              src={PlayIcon.src}
              alt="Video Play icon"
              className="!h-5 !border-none brightness-0 invert absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              width="100"
              height="80"
            />
          </div>
        );
      }

      return (
        <div ref={muxVideoRef} className="flex items-center">
          <div className="p-2 mux-video">
            <MuxVideo {...props} />
          </div>
        </div>
      );
    default:
      console.warn(
        `Invalid content type "${contentTypeId}" for carousel slide.`,
      );

      return null;
  }
});

MediaSlide.displayName = 'MediaSlide';

interface CarouselProductsProps {
  components: (TypeImage | TypeVideo | TypeMediaTextTitle)[];
  id?: string;
  classes?: string;
  name?: string;
  pageType?: string;
  customSettings?: Settings;
  showThumbnailSlider?: boolean;
  customImageWidth?: number;
}

const baseSettings: Settings = {
  dots: false,
  speed: 200,
  touchThreshold: 10,
  swipeToSlide: true,
  focusOnSelect: true,
  arrows: false,
};

const CarouselProducts: FC<CarouselProductsProps> = ({
  components = [],
  id,
  classes,
  name,
  pageType,
  customSettings,
  showThumbnailSlider = true,
  customImageWidth,
}) => {
  const { carouselImagePosition } = useCarouselContext();

  const [mainSlider, setMainSlider] = useState<Slider | null>(null);
  const [thumbnailSlider, setThumbnailSlider] = useState<Slider | null>(null);

  const mainSliderRef = useRef<Slider | null>(null);
  const thumbnailSliderRef = useRef<Slider | null>(null);

  useEffect(() => {
    setMainSlider(mainSliderRef.current);
    setThumbnailSlider(thumbnailSliderRef.current);
  }, []);

  useEffect(() => {
    if (!carouselImagePosition) return;

    // slickGoTo args (Args: index, dontAnimate)
    thumbnailSlider?.slickGoTo(carouselImagePosition, true);
  }, [carouselImagePosition]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = useCallback(() => {
    thumbnailSliderRef.current?.slickNext();
  }, []);

  const previous = useCallback(() => {
    thumbnailSliderRef.current?.slickPrev();
  }, []);

  const maxSlidesToShow = components.length;

  const slidesToShow = Math.min(maxSlidesToShow, CAROUSEL_SLIDES_TO_SHOW);

  const mainSettings: Settings = {
    ...baseSettings,
    slidesToShow: 1,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          arrows: true,
          nextArrow: (
            <ChevronIcon
              classes="mr-4 slick-arrow slick-next h-4 w-4"
              direction="right"
            />
          ),
          prevArrow: (
            <ChevronIcon
              classes="ml-4 slick-arrow slick-prev h-4 w-4"
              direction="left"
            />
          ),
          dots: true,
        },
      },
    ],
  };

  const thumbnailSettings: Settings = {
    ...baseSettings,
    slidesToShow,
    className: 'slider-thumbnail',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow,
          infinite: true,
        },
      },
      {
        breakpoint: 280,
        settings: {
          slidesToShow: 0,
          initialSlide: 0,
        },
      },
    ],
  };

  return (
    <div
      data-testid={id}
      className="luv-carousel product-slider mx-auto max-w-[1020px]"
    >
      <div className="grid grid-cols-1">
        <Slider
          {...(customSettings ? customSettings : mainSettings)}
          ref={mainSliderRef}
          asNavFor={thumbnailSlider as SLIDER_TYPE}
          className={classes}
          lazyLoad="ondemand"
        >
          {components.map((content, index) => (
            <MediaSlide
              hasControls={true}
              {...content}
              key={content?.sys?.id}
              index={index}
              name={name}
              pageType={pageType}
              width={customImageWidth}
            />
          ))}
        </Slider>
        {showThumbnailSlider && (
          <div className="relative mx-auto hidden w-[80%] max-w-[820px] px-2 lg:block">
            <Slider
              {...thumbnailSettings}
              ref={thumbnailSliderRef}
              asNavFor={mainSlider as SLIDER_TYPE}
              lazyLoad="ondemand"
            >
              {components.map((content, index) => (
                <MediaSlide
                  hasControls={false}
                  {...content}
                  key={content?.sys?.id}
                  index={index}
                  isThumbnail
                  name={name}
                  pageType={pageType}
                />
              ))}
            </Slider>
            <ThumbnailArrow direction="left" onClick={previous} />
            <ThumbnailArrow direction="right" onClick={next} />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CarouselProducts);
